declare global {
    interface Window {
        Intercom?: (...args: any[]) => void
        intercomSettings?: Record<string, any>
    }
}

type IntercomAPI = {
    isBooted: () => boolean
    isReady: () => boolean
    onceReady: () => Promise<void>
    show: () => void
    hide: () => void
    shutdown: () => void
    identify: (payload: { user_id: string | number; user_hash: string } & Record<string, unknown>) => void
    update: (data?: Record<string, unknown>) => void
}

const loadScript = (appId: string): Promise<void> =>
    new Promise((resolve, reject) => {
        if(window.Intercom) {
            return resolve();
        }

        const s = document.createElement('script');
        s.async = true;
        s.src = `https://widget.intercom.io/widget/${appId}`;
        s.onload = () => resolve();
        s.onerror = reject;
        document.head.appendChild(s);
    });

export default defineNuxtPlugin(() => {
    const { public: { intercomAppId } } = useRuntimeConfig();
    let booted = false;
    let ready = false;
    const waiters: Array<() => void> = [];

    const onceReady = () => new Promise<void>(res => {
        if(ready) {
            return res();
        }

        waiters.push(res);
    });

    const api: IntercomAPI = {
        isBooted: () => booted,
        isReady: () => ready,
        onceReady,
        show: () => window.Intercom?.('show'),
        hide: () => window.Intercom?.('hide'),
        shutdown: () => {
            window.Intercom?.('shutdown');
            booted = false;
            ready = false;
        },
        identify: (payload) => {
            window.Intercom?.('update', payload);
        },
        update: (data) => {
            window.Intercom?.('update', data || {});
        }
    }

    if(import.meta.client && intercomAppId) {
        window.intercomSettings = { app_id: intercomAppId };

        loadScript(intercomAppId).then(() => {
            window.Intercom?.('boot', window.intercomSettings);
            booted = true;

            // mark ready as soon as Intercom is responsive
            window.Intercom?.('onShow', () => {
                ready = true;
                waiters.splice(0).forEach(fn => fn());
            });
            // fallback: resolve readiness quickly even if user hasn't opened the messenger
            setTimeout(() => {
                ready = true;
                waiters.splice(0).forEach(fn => fn());
            }, 0);
        })
    }

    return {
        provide: { intercom: api }
    }
})
